import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';

import type { GameCode, MultiGameBadgesResult, Platform } from '@/types/qriots';
import { BadgeChip } from './BadgeChip';
import { FreshnessLabel } from './FreshnessLabel';

interface MultiGameBadgesCardProps {
  data: MultiGameBadgesResult;
  freshness: { fetchedAt: string; ttlSeconds: number };
  region: Platform;
  onSwitchRegion: () => void;
  onPickGame: (game: GameCode) => void;
  onEditId: () => void;
}

const GAME_EMOJI: Record<GameCode, string> = {
  lol: '🎯',
  tft: '♟',
  lor: '🃏',
};

export function MultiGameBadgesCard({
  data,
  freshness,
  region,
  onSwitchRegion,
  onPickGame,
  onEditId,
}: MultiGameBadgesCardProps) {
  const { t } = useTranslation('riot');

  return (
    <div className="mx-4 flex flex-col gap-4 rounded-xl bg-bg-secondary p-4">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline gap-1.5">
          <h2 className="truncate text-base font-bold text-fg">
            {data.gameName}
            <span className="text-fg-muted">#{data.tagLine}</span>
          </h2>
          <span className="text-xs text-fg-muted">· {region}</span>
          <button
            type="button"
            onClick={onSwitchRegion}
            className="ml-1 rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-fg-muted active:opacity-80"
          >
            {t('cta.switchRegion')}
          </button>
        </div>
        <FreshnessLabel freshness={freshness} />
      </div>

      {/* Summary */}
      <div className="text-sm text-fg">
        <span>🎮 {t('summary.plays', { played: data.summary.gamesPlayed })}</span>
        {data.summary.apexCount > 0 && (
          <span> · 👑 {t('summary.apex', { count: data.summary.apexCount })}</span>
        )}
        {data.summary.highestTier && (
          <span> · {t('summary.bestTier', { tier: data.summary.highestTier })}</span>
        )}
      </div>

      {/* Highlights */}
      {data.highlightBadges.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
            {t('title.highlights')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.highlightBadges.map((b) => (
              <BadgeChip key={b.code} badge={b} size="md" />
            ))}
          </div>
        </div>
      )}

      {/* Per-game rows */}
      <div className="flex flex-col gap-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
          {t('title.games')}
        </p>
        {(['lol', 'tft', 'lor'] as const).map((game) => {
          const g = data.byGame[game];
          const played = g.played;
          const label = t(`games.${game}`);
          const level = g.summonerLevel ? ` · Lv${g.summonerLevel}` : '';
          return (
            <button
              key={game}
              type="button"
              disabled={!played || game !== 'lol'}
              onClick={() => onPickGame(game)}
              className="flex items-center justify-between rounded-lg bg-bg-tertiary px-3 py-2 text-sm text-fg transition-opacity active:opacity-80 disabled:opacity-50"
            >
              <span>
                <span className="mr-2" aria-hidden>{GAME_EMOJI[game]}</span>
                <span className="font-medium">{label}</span>
                {played ? (
                  <span className="text-fg-muted">{level}</span>
                ) : (
                  <span className="text-fg-muted"> · {t('games.notPlayed')}</span>
                )}
              </span>
              {played && game === 'lol' && (
                <ChevronRight aria-hidden className="h-4 w-4 text-fg-muted" />
              )}
            </button>
          );
        })}
      </div>

      {/* Edit ID */}
      <button
        type="button"
        onClick={onEditId}
        className="rounded-lg border border-border bg-bg-tertiary py-2 text-xs font-medium text-fg-muted transition-opacity active:opacity-80"
      >
        {t('cta.editId')}
      </button>

      {/* Disclaimer */}
      <p className="text-[10px] leading-relaxed text-fg-muted">
        {t('disclaimer.riot')}
      </p>
    </div>
  );
}
